import type { Account } from "@eai-toolkit/client";
import { useMemo } from "react";

import { useGetAllAccountsQuery } from "../api/accountsApi";

type Options = {
  permission?: string;
  skip?: boolean;
};

/**
 * Hook that fetches accounts and returns both the list and a Map for lookups.
 *
 * The Map allows looking up accounts by both ID and fullName, which is useful
 * for components that need to resolve account references.
 */
export function useAccountsWithMap(options?: Options) {
  // Use a limit of 1000 because right now there are <1000 accounts (but >500) and it's nice to just need one API call (rather than two, for 500 each).
  const queryResult = useGetAllAccountsQuery(
    {
      limit: 1000,
      permission: options?.permission,
    },
    { skip: options?.skip },
  );

  const accountsList = useMemo(
    () => queryResult.data ?? [],
    [queryResult.data],
  );

  const accountsMap = useMemo(() => {
    const map = new Map<string, Account>();
    accountsList.forEach((account) => {
      map.set(account.id, account);
      if (account.fullName) {
        map.set(account.fullName, account);
      }
    });
    return map;
  }, [accountsList]);

  return {
    ...queryResult,
    accountsList,
    accountsMap,
  };
}
